package com.trevorwiebe.timesheet.settlements.domain

import com.trevorwiebe.timesheet.admin.reporting.domain.calculateGeneralMileageReport
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import com.trevorwiebe.timesheet.invoicing.domain.toInvoiceLine
import com.trevorwiebe.timesheet.invoicing.domain.unitDetail
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.hasCarrier
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementLine
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementStatus
import kotlinx.datetime.LocalDate

/** Loads already on a settlement (draft or sent) are never offered again. */
fun settledLoadIds(settlements: List<Settlement>): Set<String> =
    settlements.flatMapTo(mutableSetOf()) { it.loadIds }

fun driversOf(users: List<DatabaseUserModel>, ownerOperatorId: String): List<DatabaseUserModel> =
    users.filter { it.ownerOperatorId == ownerOperatorId && !it.deleted }

fun OwnerOperator.settlementName(): String = companyName.ifBlank { contactName }

fun Carrier.settlementName(): String = companyName.ifBlank { contactName }

/** Carriers keep the fuel surcharge and other charges in full, and this share of the base rate. */
const val CARRIER_BASE_PAY_PERCENT = 95.0

/**
 * Delivered loads driven by [driverIds] that aren't on a settlement yet, oldest first. A null
 * [start] means every outstanding load. Loads given to an outside carrier are never settled here.
 */
fun settleableLoads(
    loads: List<Load>,
    settledIds: Set<String>,
    driverIds: Set<String>,
    start: LocalDate?,
    end: LocalDate,
    today: LocalDate,
): List<Load> = unsettledLoads(loads, settledIds, start, end, today) { it.employeeId in driverIds && !it.hasCarrier }

/** Delivered loads hauled by [carrierId] that aren't on a settlement yet, oldest first. */
fun carrierSettleableLoads(
    loads: List<Load>,
    settledIds: Set<String>,
    carrierId: String,
    start: LocalDate?,
    end: LocalDate,
    today: LocalDate,
): List<Load> = unsettledLoads(loads, settledIds, start, end, today) { it.carrierId == carrierId }

private fun unsettledLoads(
    loads: List<Load>,
    settledIds: Set<String>,
    start: LocalDate?,
    end: LocalDate,
    today: LocalDate,
    belongs: (Load) -> Boolean,
): List<Load> = loads
    .filter { load ->
        val date = load.scheduledDate ?: return@filter false
        belongs(load) &&
            load.id !in settledIds &&
            date <= today &&
            date <= end &&
            (start == null || date >= start)
    }
    .sortedWith(compareBy({ it.scheduledDate }, { it.loadNumber }))

fun Load.toSettlementLine(locations: List<Location>, driverName: String, basePayPercent: Double): SettlementLine {
    val line = toInvoiceLine(locations)
    return SettlementLine(
        loadId = id,
        loadNumber = loadNumber,
        date = line.date,
        driverId = employeeId,
        driverName = driverName,
        pickup = line.pickup,
        drop = line.drop,
        base = line.base,
        fuelSurcharge = line.fuelSurcharge,
        detention = line.detention,
        stopOff = line.stopOff,
        other = line.other,
        basePayPercent = basePayPercent,
        unitDetail = rate?.unitDetail().orEmpty(),
    )
}

/**
 * A draft's lines rebuilt from their loads as they are now, keeping each line's driver and pay
 * percentages. A line whose load is gone, or whose locations haven't loaded yet, is kept as it was.
 */
fun refreshedSettlementLines(settlement: Settlement, loads: List<Load>, locations: List<Location>): List<SettlementLine> {
    if (locations.isEmpty()) return settlement.lines
    val byId = loads.associateBy { it.id }
    return settlement.lines.map { line ->
        val load = byId[line.loadId] ?: return@map line
        load.toSettlementLine(locations, line.driverName, line.basePayPercent).copy(
            driverId = line.driverId,
            fuelSurchargePayPercent = line.fuelSurchargePayPercent,
            detentionPayPercent = line.detentionPayPercent,
            stopOffPayPercent = line.stopOffPayPercent,
            otherPayPercent = line.otherPayPercent,
        )
    }
}

/**
 * Miles the drivers logged from [start] through [end], worked out per truck the same way as the
 * General Mileage report so the numbers agree.
 */
fun settlementMiles(entries: List<MileageEntry>, driverIds: Set<String>, start: LocalDate, end: LocalDate): Long =
    calculateGeneralMileageReport(
        entries.filter { it.employeeId in driverIds && it.date.date >= start && it.date.date <= end },
        equipment = emptyList(),
    ).sumOf { it.totalMiles }

fun buildDraftSettlement(
    ownerOperator: OwnerOperator,
    lines: List<SettlementLine>,
    miles: Long,
    settlementDate: LocalDate,
): Settlement = Settlement(
    id = "",
    number = 0,
    ownerOperatorId = ownerOperator.id,
    ownerOperatorName = ownerOperator.settlementName(),
    settlementDate = settlementDate,
    lines = lines,
    miles = miles,
)

fun buildDraftCarrierSettlement(carrier: Carrier, lines: List<SettlementLine>, settlementDate: LocalDate): Settlement =
    Settlement(
        id = "",
        number = 0,
        ownerOperatorId = "",
        ownerOperatorName = carrier.settlementName(),
        settlementDate = settlementDate,
        lines = lines,
        carrierId = carrier.id,
    )

/** Money is whole cents. */
data class DeductionTotal(val description: String, val thisSettlement: Int, val yearToDate: Int) {
    val thisSettlementFormatted: String get() = "$" + thisSettlement.formatCentsAsDollars()
    val yearToDateFormatted: String get() = "$" + yearToDate.formatCentsAsDollars()
}

/**
 * Each deduction on [settlement] with its year-to-date total: this settlement plus the owner
 * operator's earlier sent settlements in the same calendar year. Deductions match by name,
 * ignoring case and surrounding spaces. Names only taken earlier in the year are listed too.
 */
fun deductionsYearToDate(settlement: Settlement, all: List<Settlement>): List<DeductionTotal> {
    val earlier = all.filter {
        it.id != settlement.id &&
            it.ownerOperatorId == settlement.ownerOperatorId &&
            it.status == SettlementStatus.SENT &&
            it.settlementDate.year == settlement.settlementDate.year &&
            (it.settlementDate < settlement.settlementDate ||
                (it.settlementDate == settlement.settlementDate && it.number < settlement.number))
    }
    fun key(name: String) = name.trim().lowercase()
    val names = LinkedHashMap<String, String>()
    (settlement.deductions + earlier.sortedBy { it.number }.flatMap { it.deductions })
        .forEach { names.getOrPut(key(it.description)) { it.description.trim() } }
    return names.map { (key, name) ->
        val now = settlement.deductions.filter { key(it.description) == key }.sumOf { it.amount }
        val before = earlier.flatMap { it.deductions }.filter { key(it.description) == key }.sumOf { it.amount }
        DeductionTotal(name, thisSettlement = now, yearToDate = now + before)
    }
}
